/**
 * Task 151-B — pure state model for the package create/edit drawer.
 *
 * Bridges the agency's active services + an (optional) existing package
 * into the drawer's per-service line state, and builds the
 * `setAgencyPackageServices` payload back out. Kept React-free so the
 * hydrate ↔ serialize round-trip is unit-testable.
 *
 * `enabled` is pure MEMBERSHIP: whether the service is a line in the
 * package composition. The line's commercial `included` flag and the
 * other V2 commercial columns are flattened into the line state and are
 * editable via the drawer's "advanced terms" section — so a stored
 * `included: false` line round-trips unchanged (it is NOT dropped, and
 * NOT silently re-included).
 *
 * Paid-ads coherence: for `ads`-kind lines the legacy pricing columns
 * (pricing_model / retainer_amount / spend_percentage) are DERIVED from
 * the `config` ads pricing at save time, so `config` and the typed
 * columns can never contradict.
 */

import type { AdPlatformKey, PackageServiceType } from '@kmi/contracts';
import type {
  AgencyPackageRow,
  AgencyServiceRow,
  PackageServiceDeliverable,
  PackageServiceLine,
  PricingModel,
  ServiceCadence,
  SetAgencyPackageServicesInput,
  SystemCapabilityKey,
} from '../../api/types';
import { packageServiceToDraft } from './draft';
import {
  canonicalKindForService,
  defaultConfig,
  defaultKindForCapability,
  normalizeConfig,
  resolveKind,
  serializeConfig,
} from './packageServiceConfigModel';

export interface DrawerLineState {
  readonly service_id: string;
  readonly service_name: string;
  readonly system_capability_key: SystemCapabilityKey;
  /** Membership: is this service a line in the package composition? */
  readonly enabled: boolean;
  readonly kind: PackageServiceType;
  /** Raw `{ kind, ...fields }` config object (151-A jsonb shape). */
  readonly config: Record<string, unknown>;
  // Editable V2 commercial terms (the "advanced terms" section).
  readonly included: boolean;
  readonly quantity: number | null;
  readonly notes: string | null;
  readonly deliverables: ReadonlyArray<PackageServiceDeliverable>;
  readonly pricing_model: PricingModel | null;
  readonly retainer_amount: number | null;
  readonly retainer_currency: string;
  readonly spend_percentage: number | null;
  readonly cadence: ServiceCadence | null;
  readonly sort_order: number;
  readonly terms_notes: string | null;
}

/**
 * Builds one drawer line per ACTIVE agency service, in the services'
 * own order. Services already in `pkg` are enabled (members) with their
 * persisted config hydrated (kind resolution priority — Task 167:
 *   1. canonical-by-name override for first-class default services
 *      (`canonicalKindForService(svc.name)`),
 *   2. stored `config.kind` discriminator if valid (`readKind`),
 *   3. capability-derived default (`defaultKindForCapability`))
 * and their commercial terms — including the `included` flag —
 * hydrated as-is; everything else starts as a non-member with a
 * default config (same canonical-first → capability-default priority)
 * + empty terms.
 */
export function buildDrawerLines(
  activeServices: ReadonlyArray<AgencyServiceRow>,
  pkg: AgencyPackageRow | null,
): DrawerLineState[] {
  const byId = new Map<string, PackageServiceLine>();
  for (const line of pkg?.services ?? []) byId.set(line.service_id, line);

  return activeServices.map((svc, idx) => {
    const existing = byId.get(svc.id);
    if (existing) {
      // Task 167 — pass `svc.name` so the canonical override for first-class
      // default services (e.g. `إدارة السوشال ميديا والمحتوى` → `social`)
      // wins over a stale saved `config.kind` (e.g. `content` from a wrong
      // bootstrap). For non-canonical / custom services the call falls
      // through to `readKind(existing.config) ?? defaultKindForCapability(cap)`,
      // matching the pre-167 behavior.
      const kind = resolveKind(existing.config, svc.system_capability_key, svc.name);
      const d = packageServiceToDraft(existing);
      return {
        service_id: svc.id,
        service_name: svc.name,
        system_capability_key: svc.system_capability_key,
        // Membership: present in the package (regardless of `included`).
        enabled: true,
        kind,
        config: serializeConfig(kind, normalizeConfig(kind, existing.config)),
        included: existing.included,
        quantity: d.quantity,
        notes: d.notes,
        deliverables: d.deliverables,
        pricing_model: d.pricing_model,
        retainer_amount: d.retainer_amount,
        retainer_currency: d.retainer_currency || 'SAR',
        spend_percentage: d.spend_percentage,
        cadence: d.cadence,
        sort_order: d.sort_order,
        terms_notes: d.terms_notes,
      };
    }
    // Task 167 — for new (not-yet-saved) lines the canonical override
    // applies to the same first-class default services. Falls back to
    // `defaultKindForCapability(svc.system_capability_key)` for custom
    // services where no canonical mapping exists.
    const kind =
      canonicalKindForService(svc.name) ?? defaultKindForCapability(svc.system_capability_key);
    return {
      service_id: svc.id,
      service_name: svc.name,
      system_capability_key: svc.system_capability_key,
      enabled: false,
      kind,
      config: serializeConfig(kind, defaultConfig(kind)),
      included: true,
      quantity: null,
      notes: null,
      deliverables: [],
      pricing_model: null,
      retainer_amount: null,
      retainer_currency: 'SAR',
      spend_percentage: null,
      cadence: null,
      sort_order: idx,
      terms_notes: null,
    };
  });
}

export interface LegacyPricing {
  readonly pricing_model: PricingModel;
  readonly retainer_amount: number | null;
  readonly spend_percentage: number | null;
}

/**
 * For an `ads`-kind line, derive the legacy commercial pricing columns
 * from the `config` ads pricing so the two can never contradict:
 *   fixed      → pricing_model 'fixed'                 + retainer_amount
 *   percentage → pricing_model 'percentage_of_spend'   + spend_percentage
 *   hybrid     → pricing_model 'fixed_plus_percentage' + both
 * Returns `null` for non-ads kinds (they keep their advanced-terms
 * pricing untouched).
 */
export function deriveLegacyPricingFromConfig(
  kind: PackageServiceType,
  config: Record<string, unknown>,
): LegacyPricing | null {
  if (kind !== 'ads') return null;
  const ads = normalizeConfig('ads', config);
  switch (ads.pricing) {
    case 'percentage':
      return {
        pricing_model: 'percentage_of_spend',
        retainer_amount: null,
        spend_percentage: ads.percentage,
      };
    case 'hybrid':
      return {
        pricing_model: 'fixed_plus_percentage',
        retainer_amount: ads.amount,
        spend_percentage: ads.percentage,
      };
    case 'fixed':
    default:
      return { pricing_model: 'fixed', retainer_amount: ads.amount, spend_percentage: null };
  }
}

/**
 * Builds the `setAgencyPackageServices` input from the drawer lines:
 * only enabled (member) services are included (this EF replaces the
 * whole composition), each with a clean serialized `config`, its own
 * `included` flag + sort_order, and its commercial terms. For ads-kind
 * lines the legacy pricing columns are derived from config so the saved
 * payload is internally coherent.
 *
 * Task 161B note: `deliverables` is round-tripped UNCHANGED from drawer
 * state. The Task 161B family layout HIDES `DeliverablesEditor` from
 * the main flow for typed-config + influencer-scope families, but the
 * drawer separately surfaces any persisted legacy rows under a
 * "تسليمات قديمة" section so the manager has a visible cleanup path —
 * silent cleanup at the save boundary would be data loss (a no-op open
 * + save would erase persisted deliverables). The save path stays a
 * faithful mirror of drawer state.
 */
export function buildSetServicesInput(
  packageId: string,
  lines: ReadonlyArray<DrawerLineState>,
): SetAgencyPackageServicesInput {
  const enabled = lines.filter((l) => l.enabled);
  return {
    package_id: packageId,
    services: enabled.map((l) => {
      const derived = deriveLegacyPricingFromConfig(l.kind, l.config);
      return {
        service_id: l.service_id,
        quantity: l.quantity,
        notes: l.notes,
        deliverables: l.deliverables,
        pricing_model: derived ? derived.pricing_model : l.pricing_model,
        retainer_amount: derived ? derived.retainer_amount : l.retainer_amount,
        retainer_currency: l.retainer_currency,
        spend_percentage: derived ? derived.spend_percentage : l.spend_percentage,
        cadence: l.cadence,
        included: l.included,
        sort_order: l.sort_order,
        terms_notes: l.terms_notes,
        config: serializeConfig(l.kind, normalizeConfig(l.kind, l.config)),
      };
    }),
  };
}

/**
 * The de-duplicated ad-platform keys across a package's `ads`-kind
 * lines — used by the package card's "ad platforms" chips.
 */
export function packageAdPlatforms(pkg: AgencyPackageRow): AdPlatformKey[] {
  const out: AdPlatformKey[] = [];
  const seen = new Set<string>();
  for (const line of pkg.services) {
    if (resolveKind(line.config, line.system_capability_key) !== 'ads') continue;
    for (const p of normalizeConfig('ads', line.config).platforms) {
      if (!seen.has(p)) {
        seen.add(p);
        out.push(p);
      }
    }
  }
  return out;
}
